import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class MailRequest extends BaseObj {
    public Message: string | null;
    public __message: string | null;

    public Recipient: string | null;
    public __recipient: string | null;

    public Sender: string | null;
    public __sender: string | null;

    public SenderName: string | null;
    public __sendername: string | null;

    public Sent: Date | null;
    public __sent: Date | null;

    public Subject: string | null;
    public __subject: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        EntityState?: EntityState;
        Message?: string | null;
        Recipient?: string | null;
        Sender?: string | null;
        SenderName?: string | null;
        Sent?: Date | null;
        Subject?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<MailRequest>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('MailRequest tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.Message = values.Message ?? null;
        this.__message = values.Message ?? null;
        Object.defineProperty(this, 'Message', {
            get(): string | null {
                return this.__message;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Message', this.__message, value);
                this.__message = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Recipient = values.Recipient ?? null;
        this.__recipient = values.Recipient ?? null;
        Object.defineProperty(this, 'Recipient', {
            get(): string | null {
                return this.__recipient;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Recipient', this.__recipient, value);
                this.__recipient = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sender = values.Sender ?? null;
        this.__sender = values.Sender ?? null;
        Object.defineProperty(this, 'Sender', {
            get(): string | null {
                return this.__sender;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Sender', this.__sender, value);
                this.__sender = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SenderName = values.SenderName ?? null;
        this.__sendername = values.SenderName ?? null;
        Object.defineProperty(this, 'SenderName', {
            get(): string | null {
                return this.__sendername;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('SenderName', this.__sendername, value);
                this.__sendername = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sent = values.Sent ?? null;
        this.__sent = values.Sent ?? null;
        Object.defineProperty(this, 'Sent', {
            get(): Date | null {
                return this.__sent;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Sent', this.__sent, value);
                this.__sent = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Subject = values.Subject ?? null;
        this.__subject = values.Subject ?? null;
        Object.defineProperty(this, 'Subject', {
            get(): string | null {
                return this.__subject;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Subject', this.__subject, value);
                this.__subject = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"Message": ${JSON.stringify(this.Message)}` +
            ',' +
            `"Recipient": ${JSON.stringify(this.Recipient)}` +
            ',' +
            `"Sender": ${JSON.stringify(this.Sender)}` +
            ',' +
            `"SenderName": ${JSON.stringify(this.SenderName)}` +
            ',' +
            `"Sent": ${this.Sent ? '"' + formatDateIso(this.Sent) + '"' : null}` +
            ',' +
            `"Subject": ${JSON.stringify(this.Subject)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of MailRequest from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Message?: string | null;
        Recipient?: string | null;
        Sender?: string | null;
        SenderName?: string | null;
        Sent?: Date | null;
        Subject?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): MailRequest {
        return new MailRequest(values);
    }

    public static _FromServerJson(json: any): MailRequest {
        return new MailRequest({
            Message: json.Message ?? '',
            Recipient: json.Recipient ?? '',
            Sender: json.Sender ?? '',
            SenderName: json.SenderName ?? '',
            Sent: json.Sent ? (json.Sent instanceof Date ? json.Sent : new Date(json.Sent)) : null,
            Subject: json.Subject ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of MailRequest from another instance of MailRequest.
     * @param other The MailRequest you want to clone
     * @returns A cloned MailRequest
     */
    public static FromOther(other: MailRequest): MailRequest {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return MailRequest._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return MailRequest.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('Message' | 'Recipient' | 'Sender' | 'SenderName' | 'Sent' | 'Subject')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'MailRequest';
}
